import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';
import { useEffect, useRef } from 'react';

function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const scrollY = window.scrollY;
      const opacity = Math.max(0, 1 - scrollY / 600);
      heroRef.current.style.opacity = String(opacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[90vh] flex items-center overflow-hidden bg-gp-graphite"
    >
      <div className="absolute inset-0">
        <img
          src="/images/LOCACAO-EQUIPAMENTOS-INDUSTRIAIS/HERO/HERO-LOCACAO.webp"
          alt="Locação de equipamentos industriais hidráulicos prontos para operação - Grupo Pilar"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gp-graphite/75"></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-24">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Locação Industrial</span>
          </div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase leading-[0.95] mb-8">
            Locação de<br />
            <span className="text-gp-yellow">Equipamentos Industriais</span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-xl leading-relaxed mb-10">
            Soluções de locação para apoio à manutenção industrial, paradas programadas, testes operacionais e demandas temporárias de operação.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <div className="w-9 h-9 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
              </div>
              <span>Chamar no WhatsApp</span>
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a href="#termovacuo" className="text-gray-500 hover:text-gp-yellow transition-colors duration-300">
          <i className="ri-arrow-down-line text-2xl w-8 h-8 flex items-center justify-center"></i>
        </a>
      </div>
    </section>
  );
}

function UnitSection() {
  return (
    <section id="termovacuo" className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className="w-full lg:w-1/2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gp-yellow/10 rounded-lg"></div>
              <img
                src={ASSETS.termovacuo}
                alt="Unidade Termovácuo TFI-1000 para purificação de óleo hidráulico"
                className="relative w-full h-[400px] md:h-[500px] object-cover rounded-lg"
              />
              <div className="absolute bottom-4 left-4 bg-gp-yellow text-gp-graphite px-4 py-2 rounded-sm font-semibold text-xs uppercase tracking-wider">
                TFI-1000
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Equipamento em Locação</span>
            </div>

            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider mb-4">
              Unidade de Termovácuo para Locação
            </h2>

            <p className="text-gp-yellow font-semibold text-lg uppercase tracking-wider mb-6">
              TFI-1000 — Purificador Termovácuo Linha TFI
            </p>

            <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
              Sistema desenvolvido para filtragem, desumidificação e desgaseificação de óleo hidráulico e óleo isolante, auxiliando na remoção de partículas, umidade e gases contaminantes.
            </p>

            <p className="text-gray-400 text-base leading-relaxed mb-8">
              Ideal para aplicações industriais que exigem maior confiabilidade operacional e preservação dos sistemas hidráulicos e lubrificados.
            </p>

            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Consultar Disponibilidade
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SpecsSection() {
  const specs = [
    {
      icon: 'ri-drop-line',
      label: 'Vazão',
      value: '1000',
      unit: 'LTS/H',
      description: 'Capacidade de processamento contínuo',
    },
    {
      icon: 'ri-flashlight-line',
      label: 'Alimentação',
      value: '220V',
      unit: 'Trifásico',
      description: 'Conexão elétrica padrão industrial',
    },
    {
      icon: 'ri-plug-line',
      label: 'Potência Total',
      value: '20',
      unit: 'KW',
      description: 'Potência instalada para operação',
    },
    {
      icon: 'ri-weight-line',
      label: 'Peso',
      value: '280',
      unit: 'KG',
      description: 'Peso total da unidade montada',
    },
  ];

  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Dados Técnicos</span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
            Especificações Técnicas
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-6xl mx-auto">
          {specs.map((spec) => (
            <div
              key={spec.label}
              className="group relative bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/50 rounded-lg p-8 transition-all duration-500 hover:-translate-y-2"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gp-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-t-lg"></div>

              <div className="w-14 h-14 bg-gp-yellow/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                <i className={`${spec.icon} text-gp-yellow w-7 h-7 flex items-center justify-center`}></i>
              </div>

              <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">{spec.label}</p>
              <p className="font-display text-4xl md:text-5xl font-bold text-white mb-1">
                {spec.value}
              </p>
              <p className="text-gp-yellow font-semibold text-sm uppercase tracking-wider mb-4">
                {spec.unit}
              </p>
              <p className="text-gray-400 text-sm">{spec.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ApplicationsSection() {
  const items = [
    'Sistemas hidráulicos industriais',
    'Transformadores',
    'Centrais hidráulicas',
    'Equipamentos de grande porte',
    'Sistemas lubrificados industriais',
  ];

  return (
    <section className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Onde Aplicar</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Aplicações
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item, index) => (
              <div
                key={index}
                className="group flex items-center gap-4 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg px-6 py-5 transition-all duration-300 hover:bg-gp-graphite-lighter/50"
              >
                <div className="w-10 h-10 bg-gp-yellow/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                </div>
                <span className="text-gray-200 text-sm md:text-base font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  const benefits = [
    {
      title: 'Redução da contaminação do óleo',
      description: 'Elimina partículas, umidade e gases dissolvidos que comprometem a performance do sistema.',
    },
    {
      title: 'Aumento da vida útil dos componentes',
      description: 'Óleo limpo reduz o desgaste de bombas, motores, cilindros e válvulas hidráulicas.',
    },
    {
      title: 'Menor desgaste do sistema hidráulico',
      description: 'Protege investimentos em equipamentos caros e minimiza paradas não programadas.',
    },
    {
      title: 'Apoio em manutenção preventiva',
      description: 'Recuperação do óleo em campo, sem necessidade de desmontagem do sistema.',
    },
    {
      title: 'Operação com alta eficiência industrial',
      description: 'Processamento contínuo com vazão de 1000 litros por hora para demandas intensas.',
    },
  ];

  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Vantagens</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Benefícios
            </h2>
          </div>

          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group flex flex-col md:flex-row md:items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg p-6 md:p-8 transition-all duration-300 hover:bg-gp-graphite-lighter/30"
              >
                <div className="w-12 h-12 bg-gp-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-add-line text-gp-yellow w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg md:text-xl mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden bg-gp-graphite">
      <div className="absolute inset-0">
        <img
          src="/images/LOCACAO-EQUIPAMENTOS-INDUSTRIAIS/CTA/ATENDIMENTO-TECNICO.webp"
          alt="Ambiente industrial de manutenção hidráulica"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gp-graphite/70"></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Disponibilidade Imediata</span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>

          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-wider mb-6">
            Precisa Locar um<br />
            <span className="text-gp-yellow">Equipamento Industrial?</span>
          </h2>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Entre em contato com nossa equipe e consulte disponibilidade para sua operação.
          </p>

          <a
            href="https://wa.me/5527993092922"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
              <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
            </div>
            <span>Chamar no WhatsApp</span>
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function BackToHomeSection() {
  return (
    <section className="py-10 border-t border-gp-graphite-lighter bg-gp-graphite">
      <div className="px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
          >
            <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
            Voltar para a Home
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function LocacaoPage() {
  useSeo({
    title: 'Locação de Equipamentos Industriais | Termovácuo e Filtragem | Pilar',
    description: 'Locação de equipamentos industriais para apoio à manutenção industrial, paradas programadas, testes operacionais e demandas temporárias. Termovácuo, sistemas de filtragem de óleo e soluções para indústria.',
    keywords: 'locação de equipamentos industriais, aluguel de termovácuo, locação de equipamentos para manutenção, filtragem de óleo, paradas programadas, manutenção industrial, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/locacao-equipamentos-industriais',
    ogUrl: 'https://www.pilarmanutencao.com.br/locacao-equipamentos-industriais',
    ogImage: ASSETS.termovacuo,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        <HeroSection />
        <UnitSection />
        <SpecsSection />
        <ApplicationsSection />
        <BenefitsSection />
        <CTASection />
        <BackToHomeSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}